import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fmtPHP, fmtDate, MIN_LOAN, MAX_LOAN, TERMS, computeLoan } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calculator as CalcIcon, Sparkles, ArrowRight } from "lucide-react";

export default function Calculator() {
  const [amount, setAmount] = useState(500000);
  const [months, setMonths] = useState(12);
  const navigate = useNavigate();

  const calc = useMemo(() => computeLoan(amount, months), [amount, months]);

  const schedule = useMemo(() => {
    const now = new Date();
    return Array.from({ length: months }, (_, i) => {
      const d = new Date(now);
      d.setDate(d.getDate() + 30 * (i + 1));
      return {
        n: i + 1,
        due: d.toISOString(),
        amount: calc.monthly_payment,
      };
    });
  }, [months, calc.monthly_payment]);

  const principalPct = (calc.principal / calc.total_repayment) * 100;

  return (
    <div className="space-y-6" data-testid="calculator-page">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
          Preview
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-1">
          Loan Calculator
        </h1>
        <p className="text-slate-400 text-sm mt-1">
          Flat rate 0.3% per month · amounts ₱50,000 – ₱3,000,000 · terms 6 / 12 / 24 / 36 months.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Inputs */}
        <Card className="card-glow p-6 rounded-xl bg-[#111826] border border-white/5 lg:col-span-2">
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400">Amount</Label>
                <Input
                  type="number"
                  value={amount}
                  min={MIN_LOAN}
                  max={MAX_LOAN}
                  step={10000}
                  onChange={(e) => {
                    const v = Math.min(MAX_LOAN, Math.max(MIN_LOAN, +e.target.value || 0));
                    setAmount(v);
                  }}
                  data-testid="calculator-amount-input"
                  className="w-40 h-9 bg-black/30 border-white/10 text-white font-mono text-right"
                />
              </div>
              <Slider
                value={[amount]}
                onValueChange={(v) => setAmount(v[0])}
                min={MIN_LOAN}
                max={MAX_LOAN}
                step={10000}
                data-testid="calculator-amount-slider-main"
              />
              <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono mt-2">
                <span>{fmtPHP(MIN_LOAN)}</span>
                <span>{fmtPHP(MAX_LOAN)}</span>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">
                Term
              </Label>
              <div className="grid grid-cols-4 gap-2">
                {TERMS.map((t) => (
                  <button
                    key={t}
                    data-testid={`calc-term-${t}`}
                    onClick={() => setMonths(t)}
                    className={`py-3 rounded-lg border text-sm font-semibold transition-all ${
                      months === t
                        ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-200"
                        : "bg-white/5 border-white/5 text-slate-400 hover:border-white/20"
                    }`}
                  >
                    {t} mo
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1 pt-2">
              <div className="flex items-center justify-between py-2.5 border-b border-white/5">
                <span className="text-sm text-slate-400">Monthly payment</span>
                <span className="font-mono tabular text-2xl font-bold text-white">
                  {fmtPHP(calc.monthly_payment)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-white/5">
                <span className="text-sm text-slate-400">Monthly interest (0.3%)</span>
                <span className="font-mono tabular text-amber-300 font-semibold" data-testid="calc-monthly-interest">
                  {fmtPHP(calc.monthly_interest)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2.5 border-b border-white/5">
                <span className="text-sm text-slate-400">Total interest</span>
                <span className="font-mono tabular text-amber-300 font-semibold">
                  {fmtPHP(calc.total_interest)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-slate-400">Total repayment</span>
                <span className="font-mono tabular text-emerald-300 font-bold">
                  {fmtPHP(calc.total_repayment)}
                </span>
              </div>
            </div>

            <div className="pt-2">
              <div className="h-3 rounded-full overflow-hidden flex border border-white/5">
                <div
                  className="h-full bg-emerald-500"
                  style={{ width: `${principalPct}%` }}
                  title="Principal"
                />
                <div
                  className="h-full bg-amber-400"
                  style={{ width: `${100 - principalPct}%` }}
                  title="Interest"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500 font-mono mt-2">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" /> Principal{" "}
                  {principalPct.toFixed(1)}%
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-amber-400" /> Interest{" "}
                  {(100 - principalPct).toFixed(1)}%
                </span>
              </div>
            </div>

            <Button
              onClick={() => navigate("/loans")}
              data-testid="apply-loan-button"
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
            >
              <Sparkles className="h-4 w-4 mr-2" /> Create loan with these terms
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </Card>

        {/* Schedule preview */}
        <Card className="card-glow rounded-xl bg-[#111826] border border-white/5 overflow-hidden lg:col-span-3">
          <div className="p-5 border-b border-white/5 flex items-center gap-2">
            <CalcIcon className="h-4 w-4 text-emerald-400" />
            <h2 className="font-display text-lg font-semibold text-white">Preview schedule</h2>
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-[#111826]">
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-xs uppercase text-slate-400">#</TableHead>
                  <TableHead className="text-xs uppercase text-slate-400">Due</TableHead>
                  <TableHead className="text-xs uppercase text-slate-400 text-right">
                    Amount
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedule.map((s) => (
                  <TableRow key={s.n} className="border-white/5" data-testid={`calc-row-${s.n}`}>
                    <TableCell className="font-mono text-slate-400">{s.n}</TableCell>
                    <TableCell className="text-slate-200">{fmtDate(s.due)}</TableCell>
                    <TableCell className="font-mono tabular text-right text-emerald-300 font-semibold">
                      {fmtPHP(s.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}
